import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useTheme } from "@/context/ThemeContext";
import { useSettings } from "@/context/SettingsContext";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MessageCircle, Facebook, Instagram, Music2 } from "lucide-react";

export default function Footer() {
  const { t, lang, L } = useI18n();
  const { theme, setTheme } = useTheme();
  const { settings } = useSettings();
  const wa = settings?.whatsapp || {};
  const number = (wa.contact_number || "").replace(/[^0-9]/g, "");
  const waHref = number ? `https://wa.me/${number}` : "#";

  const quick = [
    { to: "/daily", label: t("nav.daily") },
    { to: "/shop", label: L("功德店铺", "Merit Shop") },
    { to: "/lotus", label: t("nav.lotus") },
    { to: "/deity", label: t("nav.deity") },
    { to: "/member", label: t("nav.member") },
  ];
  const socials = [
    { key: "facebook", label: "Facebook", Icon: Facebook },
    { key: "instagram", label: "Instagram", Icon: Instagram },
    { key: "tiktok", label: "TikTok", Icon: Music2 },
  ].map((s) => ({ ...s, url: (settings?.social?.[s.key] || "").trim() }));
  const activeSocials = socials.filter((s) => s.url);
  const themeOpts = [
    { v: "default", label: L("古典金", "Classic"), sw: "42 62% 52%" },
    { v: "vintage", label: L("时尚古代", "Vintage"), sw: "36 58% 52%" },
    { v: "youth", label: L("年轻时尚", "Youth"), sw: "186 62% 28%" },
  ];

  return (
    <footer className="mt-16 border-t border-border bg-[hsl(var(--secondary))]">
      <div className="h-0.5 w-full bg-[hsl(var(--brand-gold))] opacity-70" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/assets/logo.png" alt="CiAO Merit" className="h-12 w-12 rounded-full ring-1 ring-[hsl(var(--brand-gold))]" />
            <div>
              <div className="font-display text-lg font-bold">{t("brand")}</div>
              <div className="text-[10px] tracking-[0.25em] text-[hsl(var(--brand-bronze))] uppercase">{t("brandSub")}</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-6 max-w-xs">{t("footer.about")}</p>
          <div className="mt-4 flex flex-col gap-2 text-sm">
            <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">{t("nav.about")}</Link>
            <Link to="/testimonials" className="text-muted-foreground hover:text-foreground transition-colors">{t("nav.testimonials")}</Link>
            <Link to="/faq" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-faq">{t("nav.faq")}</Link>
          </div>
        </div>

        {/* Quick links */}
        <div>
          <div className="font-display font-semibold mb-3">{t("footer.quickLinks")}</div>
          <div className="flex flex-col gap-2 text-sm">
            {quick.map((q) => (
              <Link key={q.to} to={q.to} data-testid={`footer-link-${q.to.slice(1)}`}
                className="text-muted-foreground hover:text-foreground transition-colors">{q.label}</Link>
            ))}
          </div>
        </div>

        {/* Social + contact */}
        <div>
          <div className="font-display font-semibold mb-3">{L("关注我们", "Follow Us")}</div>
          {activeSocials.length > 0 ? (
            <div className="flex items-center gap-3">
              {activeSocials.map(({ key, label, Icon, url }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${key}-button`}
                  className="size-10 rounded-full border border-border bg-card flex items-center justify-center text-[hsl(var(--brand-bronze))] hover:bg-[hsla(var(--accent),0.35)] hover:text-foreground transition-colors">
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{L("社群链接即将开通", "Social links coming soon")}</p>
          )}
          {number && (
            <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
              className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#25D366] transition-colors">
              <MessageCircle className="h-4 w-4" /> {t("common.whatsapp")}
            </a>
          )}
        </div>

        {/* Theme switcher */}
        <div>
          <div className="font-display font-semibold mb-3">{L("网站主题", "Theme")}</div>
          <ToggleGroup
            type="single"
            value={theme}
            onValueChange={(v) => v && setTheme(v)}
            className="flex flex-wrap justify-start gap-2"
            data-testid="footer-theme-toggle"
          >
            {themeOpts.map((o) => (
              <ToggleGroupItem
                key={o.v}
                value={o.v}
                data-testid={`footer-theme-${o.v}`}
                className="gap-2 rounded-full border border-border px-3 py-2 text-xs data-[state=on]:bg-[hsl(var(--primary))] data-[state=on]:text-primary-foreground"
              >
                <span className="inline-block h-3 w-3 rounded-full ring-1 ring-black/10" style={{ background: `hsl(${o.sw})` }} />
                {o.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <p className="mt-2 text-[11px] text-muted-foreground">{L("选择你喜欢的风格，随时切换", "Pick a style you love — switch anytime")}</p>
        </div>
      </div>

      <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {lang === "zh" ? "你好功德 CiAO Merit" : "CiAO Merit"} · {t("footer.rights")}
      </div>
    </footer>
  );
}
